import { useState, useEffect } from 'react'

export default function WatchlistPanel({ watchlist, breachAlerts, signals, onAdd, onRemove }) {
  const [newTicker, setNewTicker] = useState('')
  const [glowingTickers, setGlowingTickers] = useState(new Set())
  const [signalCounts, setSignalCounts] = useState({})

  // Calculate signal counts per ticker and track glowing state
  useEffect(() => {
    const counts = {}
    const newGlowing = new Set()

    signals.forEach(signal => {
      signal.tickers.forEach(ticker => {
        if (watchlist.includes(ticker)) {
          counts[ticker] = (counts[ticker] || 0) + 1
          // Signal from last 5 minutes triggers glow
          if (Date.now() - signal.timestamp < 5 * 60 * 1000) {
            newGlowing.add(ticker)
          }
        }
      })
    })

    setSignalCounts(counts)
    setGlowingTickers(newGlowing)

    // Clear glow after animation
    if (newGlowing.size > 0) {
      const timeout = setTimeout(() => setGlowingTickers(new Set()), 1500)
      return () => clearTimeout(timeout)
    }
  }, [signals, watchlist])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (newTicker.trim()) {
      onAdd(newTicker.trim())
      setNewTicker('')
    }
  }

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp
    const minutes = Math.floor(diff / 60000)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ago`
  }

  return (
    <aside className="panel-left">
      <div className="panel-header">
        <span className="panel-title">Watchlist</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-dim)'
        }}>
          {watchlist.length}
        </span>
      </div>

      <div className="panel-content">
        {/* Watchlist Section */}
        <div className="panel-section">
          {watchlist.map(ticker => (
            <div
              key={ticker}
              className={`watchlist-item ${glowingTickers.has(ticker) ? 'glowing' : ''}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span className="watchlist-ticker">{ticker}</span>
                {signalCounts[ticker] > 0 && (
                  <span className="watchlist-badge">{signalCounts[ticker]}</span>
                )}
              </div>
              <button
                className="watchlist-remove"
                onClick={() => onRemove(ticker)}
                title="Remove from watchlist"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}

          {watchlist.length === 0 && (
            <div style={{
              padding: 'var(--space-4)',
              color: 'var(--text-muted)',
              fontSize: 'var(--text-sm)',
              textAlign: 'center'
            }}>
              No tickers. Add some below.
            </div>
          )}
        </div>

        {/* Breach Alerts Section */}
        {breachAlerts.length > 0 && (
          <div className="panel-section">
            <div className="panel-section-header">
              <span className="panel-section-title">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-orange)" strokeWidth="2" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Alerts
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                color: 'var(--accent-red)'
              }}>
                {breachAlerts.length}
              </span>
            </div>

            {breachAlerts.map(alert => {
              const severityColor = alert.severity === 'critical' ? 'var(--accent-red)'
                : alert.severity === 'high' ? 'var(--accent-orange)'
                : 'var(--accent-yellow)'
              return (
                <div key={alert.id} className={`breach-alert severity-${alert.severity || 'medium'}`}>
                  <span className="breach-icon" style={{ color: severityColor }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  <div className="breach-content">
                    <div className="breach-ticker" style={{ color: severityColor }}>{alert.ticker}</div>
                    <div className="breach-type" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', textTransform: 'uppercase' }}>{alert.type}</div>
                    <div className="breach-message">{alert.message}</div>
                    <div className="breach-time">{formatTime(alert.timestamp)}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Add Ticker Input */}
      <form onSubmit={handleSubmit} className="watchlist-input-wrapper">
        <input
          type="text"
          className="watchlist-input"
          placeholder="+ Add ticker..."
          value={newTicker}
          onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
          maxLength={5}
        />
      </form>
    </aside>
  )
}
